import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { PaperGradientBG } from "../kits";
import useDesktop from "../hooks/useDesktop";
import usePrefersReducedMotion from "../hooks/usePrefersReducedMotion";
import useAnimationOverride from "../hooks/useAnimationOverride";
import { useTheme } from "../context/ThemeContext";

const LINES = [
  { type: "prompt", text: "$ whoami" },
  { type: "response", text: "> denis bitter — software fullstack architect" },
  { type: "prompt", text: "$ mission" },
  { type: "response", text: "> design + code" },
  { type: "response", text: "> entwickler + dozent" },
  { type: "prompt", text: "$ init --stack" },
  { type: "response", text: "> react · angular · flutter · system architecture" },
  { type: "prompt", text: "$ status" },
  { type: "status", text: "> ready" },
];

const TYPING_SPEED = 38;
const PROMPT_PAUSE = 400;
const LINE_PAUSE = 200;
const TYPEWRITER_HERO_STORAGE_KEY = "lerneinfach:typewriter-hero:done:v1";

const COMMANDS = {
  help: [
    { type: "response", text: "> verfügbare befehle:" },
    { type: "response", text: ">   help          – diese hilfe anzeigen" },
    { type: "response", text: ">   ls            – seiten auflisten" },
    { type: "response", text: ">   cd <pfad>     – zu einer seite navigieren" },
    { type: "response", text: ">   whoami        – wer ist denis?" },
    { type: "response", text: ">   clear         – terminal leeren" },
  ],
  ls: [
    { type: "response", text: "> /zenlab2       – zenlab" },
    { type: "response", text: "> /courses      – kurse" },
    { type: "response", text: "> /blog         – blog" },
    { type: "response", text: "> /contact      – sag hallo" },
    { type: "response", text: "> /about        – über mich" },
  ],
  whoami: [
    { type: "response", text: "> denis bitter" },
    { type: "response", text: "> software fullstack architect" },
    { type: "response", text: "> hamburg, deutschland" },
  ],
};

const NAV_PATHS = {
  "/zenlab2": "/zenlab2",
  "zenlab": "/zenlab2",
  "/blog": "/blog",
  "blog": "/blog",
  "/courses": "/courses",
  "courses": "/courses",
  "/contact": "/contact",
  "contact": "/contact",
  "/about": "/about",
  "about": "/about",
};

const SHORTCUTS = [
  { key: "i", label: "ich", path: "/about" },
  { key: "s", label: "sagHallo", path: "/contact" },
  { key: "z", label: "zenLab dev log", path: "/zenlab2" },
];

function TypewriterHero({ className = "", onDone }) {
  const { isDark } = useTheme();

  const t = isDark
    ? {
        bgVariant: { stops: ["#171717", "#171717"], angle: 180 },
        titleBar: "bg-[#1a1a1a]",
        terminalBg: "bg-[#0F0F0F]",
        shortcutBar: "bg-[#1a1a1a]",
        promptText: "text-[#d0cbb8]/60",
        responseText: "text-[#d0cbb8]",
        inputText: "text-[#d0cbb8]",
        hintText: "text-[#b0ac9b]/90",
        shortcutLabel: "text-[#b0ac9b]/90",
        titleLabel: "text-[#AC8E66]",
        kbdClass:       "border-[#AC8E66]/30 bg-[#AC8E66]/10 text-[#AC8E66]",
        terminalBorder: "border-[#AC8E66]/30",
      }
    : {
        bgVariant:      { stops: ["#d0cbb8", "#d0cbb8"], angle: 180 },
        titleBar:       "bg-[#b8b2a0]",
        terminalBg:     "bg-[#c8c2b0]",
        shortcutBar:    "bg-[#b8b2a0]",
        promptText:     "text-[#2a2a2a]/60",
        responseText:   "text-[#1a1a1a]",
        inputText:      "text-[#1a1a1a]",
        hintText:       "text-[#1a1a1a]/40",
        shortcutLabel:  "text-[#1a1a1a]/50",
        titleLabel:     "text-[#1a1a1a]/60",
        kbdClass:       "border-[#1a1a1a]/20 bg-[#1a1a1a]/8 text-[#1a1a1a]/70",
        terminalBorder: "border-[#1a1a1a]/50",
      };
  const [hasStoredDoneState] = useState(() => {
    if (typeof window === "undefined") return false;
    try {
      return window.sessionStorage.getItem(TYPEWRITER_HERO_STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  });

  const navigate = useNavigate();
  const isDesktop = useDesktop();
  const prefersReduced = usePrefersReducedMotion();
  const animOverride = useAnimationOverride();
  const shouldAnimate = animOverride ?? (isDesktop && !prefersReduced);
  const [visibleLines, setVisibleLines] = useState(() =>
    hasStoredDoneState ? LINES.map((line) => ({ ...line, displayText: line.text })) : []
  );
  const [currentLineIndex, setCurrentLineIndex] = useState(() => (hasStoredDoneState ? LINES.length : 0));
  const [currentCharIndex, setCurrentCharIndex] = useState(0);
  const [isTyping, setIsTyping] = useState(() => !hasStoredDoneState);
  const [done, setDone] = useState(() => hasStoredDoneState);
  const [inView, setInView] = useState(true);
  const heroRef = useRef(null);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const [interactiveLines, setInteractiveLines] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [commandHistory, setCommandHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const didFocusRef = useRef(false);
  const didNotifyRef = useRef(false);

  useEffect(() => {
    if (!shouldAnimate) {
      setVisibleLines(LINES.map((line) => ({ ...line, displayText: line.text })));
      setCurrentLineIndex(LINES.length);
      setCurrentCharIndex(0);
      setIsTyping(false);
      setDone(true);
      return;
    }
    if (!inView) return;
    if (currentLineIndex >= LINES.length) {
      setIsTyping(false);
      setDone(true);
      return;
    }

    const line = LINES[currentLineIndex];

    if (line.type === "prompt") {
      const timer = setTimeout(() => {
        setVisibleLines((prev) => [...prev, { ...line, displayText: line.text }]);
        setCurrentLineIndex((i) => i + 1);
        setCurrentCharIndex(0);
      }, currentLineIndex === 0 ? 600 : PROMPT_PAUSE);
      return () => clearTimeout(timer);
    }

    if (currentCharIndex === 0) {
      const timer = setTimeout(() => {
        setVisibleLines((prev) => [...prev, { ...line, displayText: "" }]);
        setCurrentCharIndex(1);
      }, LINE_PAUSE);
      return () => clearTimeout(timer);
    }

    if (currentCharIndex <= line.text.length) {
      const timer = setTimeout(() => {
        setVisibleLines((prev) => {
          const updated = [...prev];
          updated[updated.length - 1] = {
            ...line,
            displayText: line.text.slice(0, currentCharIndex),
          };
          return updated;
        });
        setCurrentCharIndex((c) => c + 1);
      }, TYPING_SPEED);
      return () => clearTimeout(timer);
    }

    const timer = setTimeout(() => {
      setCurrentLineIndex((i) => i + 1);
      setCurrentCharIndex(0);
    }, LINE_PAUSE);
    return () => clearTimeout(timer);
  }, [currentLineIndex, currentCharIndex, inView, shouldAnimate]);

  useEffect(() => {
    if (!done || typeof window === "undefined") return;
    try {
      window.sessionStorage.setItem(TYPEWRITER_HERO_STORAGE_KEY, "1");
    } catch {
      // Ignore storage write issues (private mode, blocked storage, etc.)
    }
  }, [done]);

  const allLines = useMemo(() => [...visibleLines, ...interactiveLines], [visibleLines, interactiveLines]);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [allLines]);

  useEffect(() => {
    if (!done) {
      didFocusRef.current = false;
      didNotifyRef.current = false;
      return;
    }
    if (!didFocusRef.current && inputRef.current) {
      inputRef.current.focus();
      didFocusRef.current = true;
    }
    if (!didNotifyRef.current) {
      onDone?.();
      didNotifyRef.current = true;
    }
  }, [done, onDone]);

  useEffect(() => {
    if (!shouldAnimate) return;
    if (!heroRef.current) return;
    const obs = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold: 0.2 }
    );
    obs.observe(heroRef.current);
    return () => obs.disconnect();
  }, [shouldAnimate]);

  const handleCommand = useCallback((cmd) => {
    const trimmed = cmd.trim().toLowerCase();
    const promptLine = { type: "prompt", displayText: `$ ${cmd.trim()}` };

    if (!trimmed) return;

    setCommandHistory((prev) => [...prev, cmd.trim()]);
    setHistoryIndex(-1);

    if (trimmed === "clear") {
      setInteractiveLines([]);
      return;
    }

    if (COMMANDS[trimmed]) {
      setInteractiveLines((prev) => [
        ...prev,
        promptLine,
        ...COMMANDS[trimmed].map((l) => ({ ...l, displayText: l.text })),
      ]);
      return;
    }

    const cdMatch = trimmed.match(/^cd\s+(.+)$/);
    if (cdMatch) {
      const target = cdMatch[1];
      const navPath = NAV_PATHS[target];
      if (navPath) {
        setInteractiveLines((prev) => [
          ...prev,
          promptLine,
          { type: "status", displayText: `> navigiere zu ${navPath}...` },
        ]);
        setTimeout(() => navigate(navPath), 600);
        return;
      }
      setInteractiveLines((prev) => [
        ...prev,
        promptLine,
        { type: "error", displayText: `> pfad "${target}" nicht gefunden. tipp "ls" für seiten.` },
      ]);
      return;
    }

    if (NAV_PATHS[trimmed] || NAV_PATHS[`/${trimmed}`]) {
      const navPath = NAV_PATHS[trimmed] || NAV_PATHS[`/${trimmed}`];
      setInteractiveLines((prev) => [
        ...prev,
        promptLine,
        { type: "status", displayText: `> navigiere zu ${navPath}...` },
      ]);
      setTimeout(() => navigate(navPath), 600);
      return;
    }

    setInteractiveLines((prev) => [
      ...prev,
      promptLine,
      { type: "error", displayText: `> command not found: ${cmd.trim()}` },
      { type: "response", displayText: `> tipp "help" oder drück [i] [s] [z]` },
    ]);
  }, [navigate]);

  const handleShortcut = useCallback((shortcut) => {
    const promptLine = { type: "prompt", displayText: `$ cd ${shortcut.path}` };
    setInteractiveLines((prev) => [
      ...prev,
      promptLine,
      { type: "status", displayText: `> navigiere zu ${shortcut.path}...` },
    ]);
    setTimeout(() => navigate(shortcut.path), 600);
  }, [navigate]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleCommand(inputValue);
      setInputValue("");
      return;
    }

    if (!inputValue && done) {
      const shortcut = SHORTCUTS.find((s) => s.key === e.key.toLowerCase());
      if (shortcut) {
        e.preventDefault();
        handleShortcut(shortcut);
        return;
      }
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();
      if (commandHistory.length === 0) return;
      const newIndex = historyIndex === -1 ? commandHistory.length - 1 : Math.max(0, historyIndex - 1);
      setHistoryIndex(newIndex);
      setInputValue(commandHistory[newIndex]);
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (historyIndex === -1) return;
      const newIndex = historyIndex + 1;
      if (newIndex >= commandHistory.length) {
        setHistoryIndex(-1);
        setInputValue("");
      } else {
        setHistoryIndex(newIndex);
        setInputValue(commandHistory[newIndex]);
      }
    }
  };

  const focusInput = () => {
    if (done && inputRef.current) {
      inputRef.current.focus();
    }
  };

  return (
    <PaperGradientBG variant={t.bgVariant}>
      <section ref={heroRef} className={`relative min-h-screen flex items-center justify-center px-4 py-12 font-mono font-light ${className}`}>
        <div className="w-full max-w-2xl mx-auto">
          {/* Terminal-Fenster */}
          <div className={`rounded-lg border ${t.terminalBorder} overflow-hidden shadow-xl shadow-[#1a1a1a]/20`}>

            {/* Title Bar */}
            <div className={`flex items-center gap-2 px-4 py-2.5 ${t.titleBar} border-b border-[#AC8E66]/10`}>
              <span className="w-3 h-3 rounded-full bg-[#ff5f57]" />
              <span className="w-3 h-3 rounded-full bg-[#febc2e]" />
              <span className="w-3 h-3 rounded-full bg-[#28c840]" />
              <span className={`ml-3 text-xs ${t.titleLabel} select-none`}>moin... aus Hamburg</span>
            </div>

            {/* Terminal Content */}
            <div
              ref={containerRef}
              className={`px-4 sm:px-6 py-5 sm:py-6 shrink-0 ${t.terminalBg} max-h-[60vh] overflow-y-auto cursor-text`}
              onClick={focusInput}
            >
              {allLines.map((line, i) => (
                <div key={i} className="leading-relaxed">
                  {line.type === "prompt" && (
                    <p className={`text-sm sm:text-sm ${t.promptText} mt-3 first:mt-0`}>
                      <span className="text-[#AC8E66]">$</span>
                      {line.displayText.slice(1)}
                    </p>
                  )}
                  {line.type === "response" && (
                    <p className={`mt-1 flex items-start gap-2 text-sm sm:text-sm ${t.responseText}`}>
                      <span className="text-[#AC8E66]/70 flex-none">&gt;</span>
                      <span className="min-w-0 flex-1">
                        {line.displayText.slice(1)}
                        {shouldAnimate && isTyping && i === visibleLines.length - 1 && (
                          <span className="inline-block w-[2px] h-[1em] bg-[#AC8E66] ml-[1px] align-middle animate-cursor-blink" />
                        )}
                      </span>
                    </p>
                  )}
                  {line.type === "error" && (
                    <p className="text-sm sm:text-sm text-[#ff6b6b] mt-1 flex items-start gap-2">
                      <span className="text-[#ff6b6b]/60 flex-none">&gt;</span>
                      <span className="min-w-0 flex-1">{line.displayText.slice(1)}</span>
                    </p>
                  )}
                  {line.type === "status" && (
                    <p className="text-sm sm:text-sm text-green-800 mt-1">
                      <span className="text-green-800/90">&gt;</span>
                      {line.displayText.slice(1)}
                      {shouldAnimate && isTyping && i === visibleLines.length - 1 && (
                        <span className="inline-block w-[2px] h-[1em] bg-[#AC8E66] ml-[1px] align-middle animate-cursor-blink" />
                      )}
                    </p>
                  )}
                </div>
              ))}

              {/* Interaktive Eingabe nach Abschluss */}
              {done && (
                <div className="mt-3 flex items-center text-sm sm:text-base">
                  <span className="text-[#AC8E66]">$</span>
                  {inputValue
                    ? <>
                        <span className={`ml-1 ${t.inputText} whitespace-pre`}>{inputValue}</span>
                        {shouldAnimate && (
                          <span className="inline-block w-[2px] h-[1.1em] bg-[#AC8E66] animate-cursor-blink flex-none" />
                        )}
                      </>
                    : <>
                        {shouldAnimate && (
                          <span className="inline-block w-[2px] h-[1.1em] bg-[#AC8E66] ml-1 animate-cursor-blink flex-none" />
                        )}
                        <span className={`${t.hintText} text-[11px] whitespace-pre ml-1`}>tippe help · ls · whoami</span>
                      </>
                  }
                  <input
                    ref={inputRef}
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    className="sr-only"
                    spellCheck={false}
                    autoComplete="off"
                  />
                </div>
              )}
            </div>

            {/* Shortcut Bar */}
            {done && (
              <div className={`flex items-center gap-3 px-4 sm:px-6 py-2.5 ${t.shortcutBar} border-t border-[#AC8E66]/10`}>
                {SHORTCUTS.map((s) => (
                  <button
                    key={s.key}
                    onClick={() => handleShortcut(s)}
                    className={`flex items-center gap-1.5 text-xs ${t.shortcutLabel} hover:opacity-80 transition-colors cursor-pointer`}
                  >
                    <kbd className={`inline-flex items-center justify-center w-5 h-5 rounded border ${t.kbdClass} text-[10px] font-mono leading-none`}>
                      {s.key}
                    </kbd>
                    <span>{s.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </PaperGradientBG>
  );
}

export default TypewriterHero;
